import OverlayOp from 'jsts/org/locationtech/jts/operation/overlay/OverlayOp';
import {
  SheetShapeDeepFragment,
  SheetScaleType,
  GetSparkelPropertiesForProjectQuery,
} from 'src/gql/graphql';
import { calculateSheetShapeFireSectioning } from 'src/domain/geometry/algorithms/sheet-shape-fire-sectioning';
import { calculateSheetShapeCornerAreas } from 'src/domain/geometry/algorithms/sheet-shape-corner';
import { calculateSheetShapeRandfelt } from 'src/domain/geometry/algorithms/sheet-shape-randfelt';
import { toMultiPolygon2 } from 'src/domain/geometry/algorithms/util/type-mapping';
import { MultiPolygon2 } from 'src/domain/geometry/geometric-types';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';
import {
  toJstsMultiPolygon,
  toMultiPolygon2 as jstsToMultiPolygon2,
} from 'src/domain/geometry/algorithms/util/polygon';
import Edit2D = Autodesk.Extensions.Edit2D;
import { ChakraColor } from 'src/utils/color-util';

interface ProtanVisualizationShapes {
  fireSectioningShapes: Autodesk.Edit2D.Shape[];
  cornerAreaShapes: Autodesk.Edit2D.Shape[];
  randAreaShapes: Autodesk.Edit2D.Shape[];
}

export function createProtanPolygonVisualizations(
  shapeModel: SheetShapeDeepFragment,
  externalShapes: SheetShapeDeepFragment[],
  calibration: SheetScaleType,
  sparkelProperties: GetSparkelPropertiesForProjectQuery | undefined,
  convertPointToRender: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  }
): ProtanVisualizationShapes {
  const result: ProtanVisualizationShapes = {
    fireSectioningShapes: [],
    cornerAreaShapes: [],
    randAreaShapes: [],
  };

  try {
    // Get the unit scale from calibration
    const unitScale = getUnitScaleFromCalibration(calibration);

    // Convert the shape's polygon to MultiPolygon2 format
    const multiPolygon2 = toMultiPolygon2(
      shapeModel.sheetShapePolygon!.multipolygon
    );

    // Get parameters from Sparkel properties
    let fireSectioningBuffer = 0;
    let roofHeight: number | undefined;

    if (sparkelProperties?.project?.sparkelProperties) {
      const bufferProperty = sparkelProperties.project.sparkelProperties.find(
        (prop: any) =>
          prop.propertySet === 'Fire sectioning' &&
          prop.dbId === shapeModel.dbId
      );
      if (bufferProperty?.propertyValue) {
        fireSectioningBuffer = Number(bufferProperty.propertyValue);
      }

      const roofHeightProperty =
        sparkelProperties.project.sparkelProperties.find(
          (prop: any) =>
            prop.propertySet === 'Roof height' && prop.dbId === shapeModel.dbId
        );
      if (roofHeightProperty?.propertyValue) {
        roofHeight = Number(roofHeightProperty.propertyValue);
      }
    }

    // Calculate fire sectioning if buffer is available
    if (fireSectioningBuffer !== undefined && fireSectioningBuffer !== null) {
      const fireSectioningResult = calculateSheetShapeFireSectioning(
        multiPolygon2,
        Number(fireSectioningBuffer / unitScale),
        externalShapes
      );

      // Calculate affected areas (difference between original and fire sectioning result)
      const originalJstsPolygon = toJstsMultiPolygon(multiPolygon2);
      const fireSectioningJstsPolygon = toJstsMultiPolygon(
        fireSectioningResult.multiPolygon
      );
      const fireSectioningDifference = OverlayOp.difference(
        originalJstsPolygon,
        fireSectioningJstsPolygon
      );

      result.fireSectioningShapes = createShapesFromJstsPolygon(
        fireSectioningDifference,
        convertPointToRender,
        {
          fillColor: ChakraColor.yellow300,
          strokeColor: ChakraColor.yellow500,
          lineWidth: 0.01,
        }
      );
    }

    // Calculate corner areas if roof height is available
    if (roofHeight !== undefined) {
      result.cornerAreaShapes = calculateCornerAreaShapes(
        multiPolygon2,
        roofHeight,
        unitScale,
        convertPointToRender
      );
    }

    // Calculate rand area if roof height is available
    if (roofHeight !== undefined) {
      result.randAreaShapes = calculateRandAreaShapes(
        multiPolygon2,
        roofHeight,
        unitScale,
        convertPointToRender
      );
    }
  } catch (error) {
    console.error('Error creating Protan polygon visualizations:', error);
  }

  return result;
}

function calculateCornerAreaShapes(
  multiPolygon2: MultiPolygon2,
  roofHeight: number,
  unitScale: number,
  convertPointToRender: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  }
): Autodesk.Edit2D.Shape[] {
  try {
    const cornerAreaResult = calculateSheetShapeCornerAreas(
      multiPolygon2,
      roofHeight,
      unitScale
    );

    const cornerAreaJstsPolygon = toJstsMultiPolygon(
      cornerAreaResult.multiPolygon
    );

    const shapes = createShapesFromJstsPolygon(
      cornerAreaJstsPolygon,
      convertPointToRender,
      {
        fillColor: ChakraColor.blue50,
        strokeColor: ChakraColor.blue500,
        lineWidth: 0.01,
      }
    );
    return shapes;
  } catch (error) {
    // Corner area algorithm fails for complex polygons with topology issues
    // This is expected behavior - skip corner areas for this shape
    if (error instanceof Error && error.message.includes('TopologyException')) {
      console.warn(
        'Corner area visualization skipped due to complex polygon geometry'
      );
    } else {
      console.error('Error calculating corner areas:', error);
    }
    return [];
  }
}

function calculateRandAreaShapes(
  multiPolygon2: MultiPolygon2,
  roofHeight: number,
  unitScale: number,
  convertPointToRender: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  }
): Autodesk.Edit2D.Shape[] {
  try {
    const randAreaResult = calculateSheetShapeRandfelt(
      multiPolygon2,
      roofHeight,
      unitScale
    );

    const randAreaJstsPolygon = toJstsMultiPolygon(randAreaResult.multiPolygon);

    const shapes = createShapesFromJstsPolygon(
      randAreaJstsPolygon,
      convertPointToRender,
      {
        fillColor: ChakraColor.blue200,
        strokeColor: ChakraColor.blue500,
        lineWidth: 0.01,
      }
    );
    return shapes;
  } catch (error) {
    console.error('Error calculating rand areas:', error);
    return [];
  }
}

function createShapesFromJstsPolygon(
  jstsPolygon: any,
  convertPointToRender: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  },
  style: {
    fillColor: string;
    strokeColor: string;
    lineWidth: number;
    fillAlpha?: number;
  }
): Autodesk.Edit2D.Shape[] {
  const shapes: Autodesk.Edit2D.Shape[] = [];

  if (!jstsPolygon || jstsPolygon.isEmpty()) {
    return shapes;
  }

  try {
    // Convert JSTS polygon to MultiPolygon2 for easier processing
    const multiPolygon2 = jstsToMultiPolygon2(jstsPolygon);

    // Create shapes from the polygons
    shapes.push(
      ...multiPolygon2.polygons.map((polygon) => {
        // Convert coordinates to render coordinate system
        const exterior =
          'M ' +
          polygon.exterior
            .map((point) => {
              const converted = convertPointToRender({
                x: point[0],
                y: point[1],
              });
              return `${converted.x},${converted.y}`;
            })
            .join(' L ');

        const interiors = polygon.interiors
          .map(
            (ring) =>
              'M ' +
              ring
                .map((point) => {
                  const converted = convertPointToRender({
                    x: point[0],
                    y: point[1],
                  });
                  return `${converted.x},${converted.y}`;
                })
                .join(' L ')
          )
          .join(' ');

        const pathData = interiors?.length
          ? `${exterior} Z ${interiors} Z`
          : `${exterior} Z`;

        const svg = `<path d="${pathData}"/>`;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const shape = Autodesk.Edit2D.Shape.fromSVG(svg);

        shape.style = {
          lineColor: style.strokeColor,
          lineAlpha: 1,
          lineWidth: style.lineWidth,
          fillColor: style.fillColor,
          fillAlpha: style.fillAlpha ?? 0.5,
        };

        return shape;
      })
    );
  } catch (error) {
    console.error('Error converting JSTS polygon to Edit2D shapes:', error);
  }

  return shapes;
}
